import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig } from 'axios';

import { isAxiosError } from 'axios';

import type { ServiceRequestConfig } from '~/services/createService';

import { rolodex } from '~/services/serviceInstances';

export type RolodexBaseQueryArg = {
  url: string;
  method?: AxiosRequestConfig['method'];
  body?: unknown;
  params?: Record<string, unknown>;
  rtkQueryOptions?: ServiceRequestConfig;
};

const normalizeUrl = (rawUrl: string) => {
  const withoutApiPrefix = rawUrl.replace(/^\/?api\/v1/, '');
  return withoutApiPrefix.startsWith('/')
    ? withoutApiPrefix
    : `/${withoutApiPrefix}`;
};

export const rolodexBaseQuery: BaseQueryFn<
  RolodexBaseQueryArg,
  unknown,
  unknown
> = async ({ url, method = 'get', body, params, rtkQueryOptions }) => {
  const path = normalizeUrl(url);
  const config: ServiceRequestConfig = {
    showErrorToast: true,
    ...rtkQueryOptions,
    params,
  };

  try {
    const normalizedMethod = String(method).toLowerCase();
    let result: unknown;
    if (normalizedMethod === 'get') {
      result = await rolodex.get(path, config);
    } else if (normalizedMethod === 'post') {
      result = await rolodex.post(path, body, config);
    } else if (normalizedMethod === 'patch') {
      result = await rolodex.patch(path, body, config);
    } else if (normalizedMethod === 'put') {
      result = await rolodex.put(path, body, config);
    } else if (normalizedMethod === 'delete') {
      result = await rolodex.remove(path, { ...config, data: body });
    } else {
      return { error: { status: 400, data: `Unsupported method: ${method}` } };
    }
    return { data: result };
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: {
          status: error.response?.status ?? 'FETCH_ERROR',
          data: error.response?.data ?? error.message,
        },
      };
    }
    return { error: { status: 'FETCH_ERROR', error: String(error) } };
  }
};
